'use client';

import '@/composition/hosts/session/note-suggestions/note-suggestions-wiring';
import {
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
  shift,
  type VirtualElement,
} from '@floating-ui/dom';
import { NoteSuggestionPopover, type NoteSuggestionPopoverProps } from '@heidi/session';
import { findFirstActiveStrategy, type NoteSuggestionsState } from '@heidi/session/data';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Extension, ReactRenderer } from '@tiptap/react';

const NOTE_SUGGESTION_ANCHOR_CLASS = [
  'note-suggestion-anchor',
  'cursor-pointer rounded-xs border px-[3px] py-px',
  'border-sunlight-200 bg-sunlight-100',
  'transition-colors duration-150 ease-out',
  'hover:border-sunlight-300 hover:bg-sunlight-200',
  'newBrandDark:border-sunlight-800 newBrandDark:bg-sunlight-950',
  'newBrandDark:hover:border-sunlight-700 newBrandDark:hover:bg-sunlight-900',
].join(' ');

// opt_in_ask preview: a diff-style preview of every edit Heidi would apply if
// the user turns on adaptive learning. The note streams as the ORIGINAL, so we
// strike the original span and render the proposed replacement immediately after
// it as an inline widget — a light before/after so the user can read exactly
// what would change. Semantic tokens keep it legible in light + dark.
// The struck original is text the user is ALREADY reading, so it must not fade
// in — animating opacity on the inline decoration blanks the word and fades it
// back, which reads as the note flickering. Transition the tint instead: the
// word stays put and the strike styling eases on around it.
const NOTE_SUGGESTION_PROPOSED_ORIGINAL_CLASS = [
  'note-suggestion-proposed-original',
  'bg-surface-hover text-text-tertiary line-through',
  'motion-safe:transition-colors motion-safe:duration-200 motion-safe:ease-out-quart',
].join(' ');

const NOTE_SUGGESTION_PROPOSED_REPLACEMENT_CLASS = [
  'note-suggestion-proposed-replacement',
  'ms-0.5 bg-entity-green-muted px-0.5 text-entity-green-foreground',
].join(' ');

// The replacement widget is NEW text, so it fades in. The class is constant, so
// the CSS animation runs when ProseMirror creates the node and never re-fires on
// a rebuild that reuses it (see the widget key below).
const NOTE_SUGGESTION_PROPOSED_ENTER_CLASS = [
  'motion-safe:animate-in motion-safe:fade-in-0',
  'motion-safe:duration-200 motion-safe:ease-out-quart motion-reduce:animate-none',
].join(' ');

const NOTE_SUGGESTION_WIDGET_KEY_PREFIX = 'note-suggestion-proposed:';

function createProposedReplacementWidget(replacement: string): HTMLElement {
  const span = document.createElement('span');
  span.className = `${NOTE_SUGGESTION_PROPOSED_REPLACEMENT_CLASS} ${NOTE_SUGGESTION_PROPOSED_ENTER_CLASS}`;
  span.setAttribute('aria-hidden', 'true');
  span.textContent = replacement;
  return span;
}

export interface NoteSuggestionHighlightOptions {
  enabled: boolean;
}

interface SuggestionPopup {
  element: HTMLElement;
  show: () => void;
  hide: () => void;
  setReference: (
    rect: () => DOMRect,
    scrollAncestors: Element[],
    contextElement: Element
  ) => void;
  destroy: () => void;
}

export interface NoteSuggestionHighlightStorage {
  suggestions: NoteSuggestionsState;
  popoverRenderer: ReactRenderer<unknown, NoteSuggestionPopoverProps> | null;
  popup: SuggestionPopup | null;
  firstMatchPos: number | null;
  popoverProps: NoteSuggestionPopoverProps | null;
  lastShownSuggestionId: string | null;
}

interface NoteSuggestionMeta {
  rebuild: true;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    noteSuggestionHighlight: {
      setNoteSuggestions: (suggestions: NoteSuggestionsState) => ReturnType;
    };
  }
}

export const noteSuggestionPluginKey = new PluginKey<DecorationSet>(
  'noteSuggestionHighlight'
);

const WORD_CHAR = /[\p{L}\p{N}_]/u;

// Whole-word substring search across the doc's text nodes. Case-insensitive by
// default (so short pairs like "pt" don't highlight inside "empty"), but the
// opt_in_ask preview opts into case-sensitive matching so it mirrors the
// backend's case-sensitive substitution exactly.
export function findWholeWordPositions(
  doc: ProseMirrorNode,
  searchText: string,
  { caseSensitive = false }: { caseSensitive?: boolean } = {}
): { from: number; to: number }[] {
  const trimmed = searchText.trim();
  const needle = caseSensitive ? trimmed : trimmed.toLowerCase();
  if (!needle) return [];
  const positions: { from: number; to: number }[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    const text = caseSensitive ? node.text : node.text.toLowerCase();
    let index = text.indexOf(needle);
    while (index !== -1) {
      const before = text[index - 1] ?? '';
      const after = text[index + needle.length] ?? '';
      if (!WORD_CHAR.test(before) && !WORD_CHAR.test(after)) {
        positions.push({ from: pos + index, to: pos + index + needle.length });
      }
      index = text.indexOf(needle, index + 1);
    }
  });
  return positions;
}

export interface SuggestionRender {
  decorations: Decoration[];
  firstMatchPos: number | null;
  popoverProps: NoteSuggestionPopoverProps | null;
}

// opt_in_ask preview: render EVERY proposed edit as an inline before/after diff.
// For each application (across all strategies) we strike the original span in the
// streamed note and append its replacement as a widget right after it. Match
// case-SENSITIVELY (and dedupe by exact original) so the preview mirrors the
// backend's case-sensitive substitution: e.g. a "Commenced"→"Started" edit at a
// sentence start must not recase a mid-sentence "commenced".
function computeProposedDiffRender(
  doc: ProseMirrorNode,
  suggestions: NonNullable<NoteSuggestionsState>
): SuggestionRender {
  const decorations: Decoration[] = [];
  const seen = new Set<string>();
  suggestions.strategies.forEach((strategy) => {
    strategy.applications.forEach((app) => {
      const key = app.original;
      if (seen.has(key)) return;
      seen.add(key);
      findWholeWordPositions(doc, app.original, { caseSensitive: true }).forEach(
        ({ from, to }, occurrence) => {
          decorations.push(
            Decoration.inline(from, to, {
              class: NOTE_SUGGESTION_PROPOSED_ORIGINAL_CLASS,
            })
          );
          decorations.push(
            Decoration.widget(
              to,
              createProposedReplacementWidget(app.replacement),
              // A keyed widget is reused across rebuilds; without a key
              // ProseMirror tears the span out and re-inserts a fresh one on
              // every `docChanged` (stream-end finalize, post-close refetch,
              // and every keystroke), which replays the fade. Keyed by
              // occurrence rather than position: positions shift on any edit
              // elsewhere in the note, which would defeat the reuse.
              {
                side: 1,
                key: `${NOTE_SUGGESTION_WIDGET_KEY_PREFIX}${app.original}#${occurrence}`,
              }
            )
          );
        }
      );
    });
  });
  return { decorations, firstMatchPos: null, popoverProps: null };
}

export function computeSuggestionRender(
  doc: ProseMirrorNode,
  suggestions: NoteSuggestionsState
): SuggestionRender {
  if (!suggestions) return { decorations: [], firstMatchPos: null, popoverProps: null };

  // Post-opt-in auto-apply streams the corrected note as plain text — no inline
  // highlight. Awareness of what changed lives in version history, not a mark.
  if (suggestions.mode === 'apply') {
    return { decorations: [], firstMatchPos: null, popoverProps: null };
  }

  if (suggestions.mode === 'opt_in_ask') {
    // Preview only: an inline before/after diff of every proposed edit. The
    // opt-in offer (Turn on / Not now) is surfaced by the adaptive-learning
    // popover, driven by the same frame — no inline tiptap popover here.
    return computeProposedDiffRender(doc, suggestions);
  }

  const active = findFirstActiveStrategy(suggestions.strategies);
  if (!active) return { decorations: [], firstMatchPos: null, popoverProps: null };
  const activeStrategy = active.strategy;

  // Dedupe applications by lowercase `original`. findWholeWordPositions is
  // case-insensitive, so running it for both "pt" and "Pt" would return every
  // match twice. Keeping the first-seen casing for display purposes.
  const needleByLower = new Map<string, { original: string; replacement: string }>();
  activeStrategy.applications.forEach((app) => {
    const key = app.original.toLowerCase();
    if (!needleByLower.has(key)) {
      needleByLower.set(key, { original: app.original, replacement: app.replacement });
    }
  });

  const decorations: Decoration[] = [];
  let firstMatchPos: number | null = null;
  let firstMatchOriginal = '';
  let firstMatchReplacement = '';
  let totalMentions = 0;

  needleByLower.forEach(({ original, replacement }) => {
    const positions = findWholeWordPositions(doc, original);
    totalMentions += positions.length;
    positions.forEach(({ from, to }) => {
      decorations.push(
        Decoration.inline(from, to, {
          class: NOTE_SUGGESTION_ANCHOR_CLASS,
          'data-strategy': activeStrategy.strategy_name,
          'data-original': original,
          'data-replacement': replacement,
        })
      );
      if (firstMatchPos === null || from < firstMatchPos) {
        firstMatchPos = from;
        firstMatchOriginal = original;
        firstMatchReplacement = replacement;
      }
    });
  });

  if (firstMatchPos === null) {
    return { decorations, firstMatchPos: null, popoverProps: null };
  }

  return {
    decorations,
    firstMatchPos,
    popoverProps: {
      original: firstMatchOriginal,
      replacement: firstMatchReplacement,
      mentionCount: totalMentions,
      suggestionId: activeStrategy.suggestion_id,
      applications: activeStrategy.applications,
    },
  };
}

// Walk DOM ancestors and collect those that scroll. Used to wire scroll
// listeners on the editor's scroll container(s) so the popup tracks
// editor-internal scrolling — Floating UI's autoUpdate can't watch them
// because our reference is a VirtualElement (no DOM to walk from).
function getScrollAncestors(start: Element): Element[] {
  const result: Element[] = [];
  let el: Element | null = start.parentElement;
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    const overflow = style.overflow + style.overflowY + style.overflowX;
    if (/(auto|scroll|overlay)/.test(overflow)) result.push(el);
    el = el.parentElement;
  }
  return result;
}

// A fixed-position element appended to document.body, positioned via Floating
// UI relative to a virtual reference. Pattern lifted from
// `slash-command.ts::createPopup` (the codebase's other widget-anchored popup),
// adapted for a single long-lived popover with auto-flip placement so it
// escapes the editor's overflow:hidden ancestors.
function createSuggestionPopup(ownerDocument: Document): SuggestionPopup {
  const element = ownerDocument.createElement('div');
  element.style.position = 'fixed';
  element.style.top = '0';
  element.style.left = '0';
  element.style.zIndex = '9999';
  element.style.opacity = '0';
  element.style.pointerEvents = 'none';
  // Fade the whole popup uniformly. Without this, the buttons' `outline`
  // variant border stays vivid as the card's bg fades, making the dismiss
  // look two-staged (card fades, then buttons disappear).
  element.style.transition = 'opacity 150ms ease-out';
  element.setAttribute('aria-hidden', 'true');
  ownerDocument.body.appendChild(element);

  let getReferenceRect: () => DOMRect = () => new DOMRect(0, 0, 0, 0);
  let contextElement: Element | undefined;
  let cleanup: (() => void) | undefined;
  let isShown = false;

  const setVisible = (visible: boolean) => {
    element.style.opacity = visible ? '1' : '0';
    element.style.pointerEvents = visible ? 'auto' : 'none';
    element.setAttribute('aria-hidden', visible ? 'false' : 'true');
  };

  const updatePosition = async () => {
    // contextElement is required for hide() to walk overflow ancestors of
    // the (virtual) reference and detect when it's clipped. Without it,
    // Floating UI falls back to body and never marks the reference hidden.
    const virtualEl: VirtualElement = {
      getBoundingClientRect: getReferenceRect,
      contextElement,
    };
    const { x, y, middlewareData } = await computePosition(virtualEl, element, {
      placement: 'top-start',
      strategy: 'fixed',
      middleware: [offset(8), flip(), shift({ padding: 8 }), hide()],
    });
    const referenceHidden = middlewareData.hide?.referenceHidden ?? false;
    setVisible(isShown && !referenceHidden);
    element.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  };

  return {
    element,
    show: () => {
      isShown = true;
      void updatePosition();
    },
    hide: () => {
      isShown = false;
      setVisible(false);
      cleanup?.();
      cleanup = undefined;
    },
    setReference: (rect, scrollAncestors, ctx) => {
      getReferenceRect = rect;
      contextElement = ctx;
      cleanup?.();
      const virtualEl: VirtualElement = {
        getBoundingClientRect: rect,
        contextElement: ctx,
      };
      const baseCleanup = autoUpdate(virtualEl, element, () => void updatePosition());
      const onScroll = () => void updatePosition();
      scrollAncestors.forEach((a) =>
        a.addEventListener('scroll', onScroll, { passive: true })
      );
      cleanup = () => {
        baseCleanup();
        scrollAncestors.forEach((a) => a.removeEventListener('scroll', onScroll));
      };
    },
    destroy: () => {
      cleanup?.();
      element.remove();
    },
  };
}

export const NoteSuggestionHighlight = Extension.create<
  NoteSuggestionHighlightOptions,
  NoteSuggestionHighlightStorage
>({
  name: 'noteSuggestionHighlight',

  addOptions() {
    return { enabled: true };
  },

  addStorage() {
    return {
      suggestions: null,
      popoverRenderer: null,
      popup: null,
      firstMatchPos: null,
      popoverProps: null,
      lastShownSuggestionId: null,
    };
  },

  addCommands() {
    return {
      setNoteSuggestions:
        (suggestions: NoteSuggestionsState) =>
        ({ tr, dispatch }) => {
          this.storage.suggestions = suggestions;
          if (dispatch) {
            dispatch(
              tr.setMeta(noteSuggestionPluginKey, {
                rebuild: true,
              } satisfies NoteSuggestionMeta)
            );
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const { enabled } = this.options;
    const storage = this.storage;
    const editor = this.editor;

    const applyRender = (doc: ProseMirrorNode): DecorationSet => {
      const render = computeSuggestionRender(doc, storage.suggestions);
      storage.firstMatchPos = render.firstMatchPos;
      storage.popoverProps = render.popoverProps;
      return DecorationSet.create(doc, render.decorations);
    };

    return [
      new Plugin<DecorationSet>({
        key: noteSuggestionPluginKey,
        state: {
          init: (_, { doc }) => (enabled ? applyRender(doc) : DecorationSet.empty),
          apply: (tr, oldDecorations, _oldState, newState) => {
            if (!enabled) return DecorationSet.empty;
            const meta = tr.getMeta(noteSuggestionPluginKey) as
              | NoteSuggestionMeta
              | undefined;
            if (meta?.rebuild || tr.docChanged) {
              return applyRender(newState.doc);
            }
            return oldDecorations.map(tr.mapping, newState.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
        view: (editorView) => {
          if (!enabled) return {};

          // ProseMirror rebuilds every plugin view (destroy + re-run view())
          // whenever the plugin set changes — e.g. when another extension
          // registers/unregisters a plugin, or during editor teardown. If that
          // rebuild happens while the editor DOM is detached from its document
          // (no <body> to mount into), createSuggestionPopup's appendChild
          // throws and trips the surrounding React error boundary, crashing the
          // note editor. Skip setup in that window; a later rebuild re-runs
          // view() once the editor is reattached. Using the editor's own
          // ownerDocument also keeps the popup in the correct document context.
          const ownerDocument = editorView.dom.ownerDocument;
          if (!ownerDocument.body) return {};

          if (!storage.popoverRenderer) {
            storage.popoverRenderer = new ReactRenderer<
              unknown,
              NoteSuggestionPopoverProps
            >(NoteSuggestionPopover, {
              props: {
                original: '',
                replacement: '',
                mentionCount: 0,
                suggestionId: undefined,
                applications: [],
              },
              editor,
            });
          }
          if (!storage.popup) {
            storage.popup = createSuggestionPopup(ownerDocument);
            storage.popup.element.appendChild(storage.popoverRenderer.element);
          }

          const sync = () => {
            if (!storage.popup || !storage.popoverRenderer) return;
            const pos = storage.firstMatchPos;
            const props = storage.popoverProps;
            if (pos === null || !props) {
              storage.popup.hide();
              storage.lastShownSuggestionId = null;
              return;
            }
            storage.popoverRenderer.updateProps(props);
            storage.popup.setReference(
              () => {
                try {
                  const coords = editorView.coordsAtPos(pos);
                  return new DOMRect(
                    coords.left,
                    coords.top,
                    0,
                    coords.bottom - coords.top
                  );
                } catch {
                  // coordsAtPos can throw transiently if the editor is mid-update.
                  return new DOMRect(0, 0, 0, 0);
                }
              },
              getScrollAncestors(editorView.dom),
              editorView.dom
            );
            storage.popup.show();

            // First time we show this specific suggestion, scroll the
            // highlight into view. The note generator's auto-scroll-to-bottom
            // typically leaves the user pinned to the end of the note, so
            // without this the popover floats next to an off-screen word.
            // Only fires on suggestion-id change so subsequent re-renders
            // (typing, atom updates) don't fight user scroll.
            const currentSuggestionId = props.suggestionId ?? null;
            if (currentSuggestionId !== storage.lastShownSuggestionId) {
              storage.lastShownSuggestionId = currentSuggestionId;
              try {
                const { node } = editorView.domAtPos(pos);
                const el = node instanceof HTMLElement ? node : node.parentElement;
                el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              } catch {
                // domAtPos can throw if pos is invalid; safe to ignore.
              }
            }
          };

          sync();

          return {
            update: () => sync(),
            destroy: () => {
              storage.popup?.destroy();
              storage.popoverRenderer?.destroy();
              storage.popup = null;
              storage.popoverRenderer = null;
            },
          };
        },
      }),
    ];
  },
});
